/**
 * Automation preferences (TMMAPP-0027 / TMMAPI-0057) — catalogue from `GET /v1/me/automation`.
 *
 * Never hardcode a default, a label, or a key list the API owns. Every string the screen
 * renders comes from this response. The SPA only distinguishes AI catalogue keys from
 * deterministic ones by the wire key's `AI_` prefix (consent writes), and maps known
 * `blockedBy` codes for copy — nothing else.
 */
import type {
  AutomationResponse,
  GroupView as WireGroup,
  ItemPatch,
  ItemView as WireItem,
  PatchRequest,
} from './generated';
import { request } from './client';

export interface AutomationItem {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  isDefault: boolean;
  thresholdBp: number | null;
  thresholdMeaning: string | null;
  blockedBy: string | null;
}

export interface AutomationGroup {
  group: string;
  items: AutomationItem[];
}

export interface AutomationCatalog {
  reviewedAt: string | null;
  groups: AutomationGroup[];
}

export type AutomationPatch = {
  items?: ItemPatch[];
  reviewed?: boolean;
};

/** Catalogue keys that belong to the AI feature set (prefix convention from the API). */
export function isAiCatalogueKey(key: string): boolean {
  return key.startsWith('AI_');
}

/** Every `ItemView` field is optional; an item without a key is dropped. */
export function normalizeAutomationItem(w: WireItem): AutomationItem | null {
  if (w.key == null || w.key.length === 0) return null;
  return {
    key: w.key,
    label: w.label ?? w.key,
    description: w.description ?? '',
    enabled: w.enabled ?? false,
    isDefault: w.isDefault ?? false,
    thresholdBp: w.thresholdBp ?? null,
    thresholdMeaning: w.thresholdMeaning ?? null,
    blockedBy: w.blockedBy != null && w.blockedBy.length > 0 ? w.blockedBy : null,
  };
}

function normalizeGroup(g: WireGroup): AutomationGroup {
  return {
    group: g.group ?? '',
    items: (g.items ?? []).flatMap((item) => {
      const normalized = normalizeAutomationItem(item);
      return normalized ? [normalized] : [];
    }),
  };
}

export function normalizeAutomationCatalog(w: AutomationResponse): AutomationCatalog {
  return {
    reviewedAt: w.reviewedAt ?? null,
    groups: (w.groups ?? []).map(normalizeGroup),
  };
}

/** Every catalogue item whose key is not an AI feature key. */
export function deterministicCatalogueItems(catalog: AutomationCatalog): AutomationItem[] {
  return catalog.groups.flatMap((g) => g.items.filter((item) => !isAiCatalogueKey(item.key)));
}

export const automationApi = {
  async get(): Promise<AutomationCatalog> {
    return normalizeAutomationCatalog(
      await request<AutomationResponse>('GET', '/v1/me/automation'),
    );
  },

  async patch(body: AutomationPatch): Promise<AutomationCatalog> {
    const payload: PatchRequest = {};
    if (body.items !== undefined) payload.items = body.items;
    if (body.reviewed !== undefined) payload.reviewed = body.reviewed;
    return normalizeAutomationCatalog(
      await request<AutomationResponse>('PATCH', '/v1/me/automation', { json: payload }),
    );
  },
};
